/**
 * Shared pictures waiting for a person (Community Assets) — a section of the
 * Dev Console's review queue.
 *
 * Two lists:
 *   - Waiting for a decision: pictures the automatic check could not clear, or
 *     every new picture where checking is manual. Approve publishes it (the
 *     worker still re-verifies the bytes first); reject takes a reason its
 *     author sees.
 *   - Reported: published pictures someone reported. Take down stops serving
 *     it and closes the report as upheld (two separately audited calls);
 *     dismiss closes the report and leaves the picture up.
 *
 * Each picture is fetched privately with the reviewer's session (never from a
 * CDN) and shown blurred until the reviewer chooses to look — a flagged picture
 * can be one nobody should see by surprise.
 */
import { useEffect, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Eye, ImageOff, Loader2, Trash2, X } from 'lucide-react';
import api, { type AdminCommunityPicture, type AdminCommunityPictureReport } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { NotificationBadge } from '@/components/ui/notification-badge';
import { useToast } from '@/components/ui/use-toast';

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong';
}

/** Why a picture is waiting, in words. Unknown codes are the check's own. */
function waitingReason(code: string | null): string {
  switch (code) {
    case 'manual_review':
      return 'Checking is manual here';
    case 'scan_unavailable':
      return 'The automatic check kept failing';
    case 'too_large_to_scan':
      return 'Too large to check automatically';
    case null:
      return 'Waiting for a decision';
    default:
      return `Flagged: ${code.replace(/_/g, ' ')}`;
  }
}

const REPORT_REASONS: Record<string, string> = {
  sexual: 'Sexual content',
  violence: 'Violence',
  hate: 'Hate',
  illegal: 'Illegal',
  copyright: 'Copyright',
  spam: 'Spam',
  other: 'Other',
};

function pictureLabel(picture: AdminCommunityPicture): string {
  return picture.title ?? picture.caption ?? 'Untitled picture';
}

/** The picture, fetched with the reviewer's session and shown from memory, blurred until asked. */
function PrivatePicture({ picture }: { picture: AdminCommunityPicture }): ReactElement {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(!picture.viewable);
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    if (!picture.viewable) return;
    let url: string | null = null;
    let cancelled = false;
    void api.adminGetCommunityPicture(picture.assetId).then((blob) => {
      if (cancelled) return;
      if (!blob) {
        setFailed(true);
        return;
      }
      url = URL.createObjectURL(blob);
      setSrc(url);
    });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [picture.assetId, picture.viewable]);

  if (src) {
    return (
      <div className="relative h-40 w-40 shrink-0 overflow-hidden rounded-lg border border-border bg-muted">
        <img
          src={src}
          alt={pictureLabel(picture)}
          className={`h-full w-full object-contain ${revealed ? '' : 'scale-110 blur-xl'}`}
        />
        {!revealed && (
          <button
            type="button"
            onClick={() => setRevealed(true)}
            className="absolute inset-0 flex items-center justify-center gap-1 text-sm font-medium text-foreground"
          >
            <Eye className="h-4 w-4" />
            Show picture
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="flex h-40 w-40 shrink-0 items-center justify-center rounded-lg border border-border bg-muted">
      {failed ? (
        <ImageOff className="h-6 w-6 text-muted-foreground" aria-label="Picture unavailable" />
      ) : (
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      )}
    </div>
  );
}

function Where({ picture, verb }: { picture: AdminCommunityPicture; verb: string }) {
  return (
    <p className="text-xs text-muted-foreground">
      In {picture.archeName ?? <span className="font-mono">{picture.archeId}</span>} · {verb}{' '}
      {new Date(picture.createdAt).toLocaleString()} · by{' '}
      <span className="font-mono">{picture.publisherUserId}</span>
    </p>
  );
}

/** A reason form shared by Reject and Take down. */
function ReasonForm({
  label,
  placeholder,
  submit,
  busy,
  onSubmit,
  onCancel,
}: {
  label: string;
  placeholder: string;
  submit: string;
  busy: boolean;
  onSubmit: (reason: string) => void;
  onCancel: () => void;
}): ReactElement {
  const [reason, setReason] = useState('');
  return (
    <form
      className="flex flex-col gap-2 pt-2 sm:flex-row"
      onSubmit={(e) => {
        e.preventDefault();
        if (reason.trim()) onSubmit(reason.trim());
      }}
    >
      <Input
        aria-label={label}
        placeholder={placeholder}
        maxLength={200}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        className="h-9 flex-1"
      />
      <Button type="submit" size="sm" variant="destructive" disabled={!reason.trim() || busy}>
        {submit}
      </Button>
      <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
    </form>
  );
}

function WaitingItem({ picture }: { picture: AdminCommunityPicture }): ReactElement {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.submissions.communityPictureReviews() });

  const approve = useMutation({
    mutationFn: () => api.adminDecideCommunityPicture(picture.assetId, 'approved'),
    onSuccess: () =>
      toast({ title: 'Approved', description: 'It is shared once its bytes are re-checked.' }),
    onError: (err) =>
      toast({ title: 'Not approved', description: errorMessage(err), variant: 'destructive' }),
    onSettled: refresh,
  });
  const reject = useMutation({
    mutationFn: (reason: string) =>
      api.adminDecideCommunityPicture(picture.assetId, 'rejected', reason),
    onSuccess: () => toast({ title: 'Rejected' }),
    onError: (err) =>
      toast({ title: 'Not rejected', description: errorMessage(err), variant: 'destructive' }),
    onSettled: refresh,
  });
  const busy = approve.isPending || reject.isPending;

  return (
    <li className="flex flex-col gap-4 p-4 sm:flex-row">
      <PrivatePicture picture={picture} />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{pictureLabel(picture)}</span>
          <Badge variant="secondary">{waitingReason(picture.moderationReason)}</Badge>
        </div>
        {picture.caption && picture.title && (
          <p className="text-sm text-muted-foreground">{picture.caption}</p>
        )}
        <Where picture={picture} verb="shared" />
        {rejecting ? (
          <ReasonForm
            label={`Why ${pictureLabel(picture)} is rejected`}
            placeholder="Why (its author sees this)"
            submit="Reject"
            busy={busy}
            onSubmit={(reason) => reject.mutate(reason)}
            onCancel={() => setRejecting(false)}
          />
        ) : (
          <div className="flex gap-2 pt-2">
            <Button size="sm" className="gap-1" disabled={busy} onClick={() => approve.mutate()}>
              <Check className="h-4 w-4" />
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="gap-1"
              disabled={busy}
              onClick={() => setRejecting(true)}
            >
              <X className="h-4 w-4" />
              Reject…
            </Button>
          </div>
        )}
      </div>
    </li>
  );
}

function ReportItem({ report }: { report: AdminCommunityPictureReport }): ReactElement {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [takingDown, setTakingDown] = useState(false);
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.submissions.communityPictureReports() });

  const takeDown = useMutation({
    // Two acts, two audited calls: remove the picture, then close the report
    // as upheld. Reviewing a report never removes anything by itself.
    mutationFn: async (reason: string) => {
      await api.adminTakeDownCommunityPicture(report.assetId, reason);
      await api.adminReviewCommunityPictureReport(report.reportId, 'actioned', reason);
    },
    onSuccess: () => toast({ title: 'Taken down', description: 'The report is closed as upheld.' }),
    onError: (err) =>
      toast({ title: 'Not taken down', description: errorMessage(err), variant: 'destructive' }),
    onSettled: refresh,
  });
  const dismiss = useMutation({
    mutationFn: () => api.adminReviewCommunityPictureReport(report.reportId, 'dismissed'),
    onSuccess: () => toast({ title: 'Report dismissed' }),
    onError: (err) =>
      toast({ title: 'Not dismissed', description: errorMessage(err), variant: 'destructive' }),
    onSettled: refresh,
  });
  const busy = takeDown.isPending || dismiss.isPending;
  const picture = report.asset;
  const live = picture?.status === 'published';

  return (
    <li className="flex flex-col gap-4 p-4 sm:flex-row">
      {picture ? (
        <PrivatePicture picture={picture} />
      ) : (
        <div className="flex h-40 w-40 shrink-0 items-center justify-center rounded-lg border border-border bg-muted">
          <ImageOff className="h-6 w-6 text-muted-foreground" aria-label="Picture gone" />
        </div>
      )}
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{picture ? pictureLabel(picture) : report.assetId}</span>
          <Badge variant="destructive">{REPORT_REASONS[report.reason] ?? report.reason}</Badge>
          {picture && !live && <Badge variant="outline">{picture.status.replace(/_/g, ' ')}</Badge>}
        </div>
        {report.details && <p className="text-sm">“{report.details}”</p>}
        {picture && <Where picture={picture} verb="shared" />}
        <p className="text-xs text-muted-foreground">
          Reported {new Date(report.createdAt).toLocaleString()}
        </p>
        {takingDown ? (
          <ReasonForm
            label="Why it is taken down"
            placeholder="Why (kept with the takedown)"
            submit="Take down"
            busy={busy}
            onSubmit={(reason) => takeDown.mutate(reason)}
            onCancel={() => setTakingDown(false)}
          />
        ) : (
          <div className="flex gap-2 pt-2">
            {live && (
              <Button
                size="sm"
                variant="destructive"
                className="gap-1"
                disabled={busy}
                onClick={() => setTakingDown(true)}
              >
                <Trash2 className="h-4 w-4" />
                Take down…
              </Button>
            )}
            <Button size="sm" variant="outline" disabled={busy} onClick={() => dismiss.mutate()}>
              {live ? 'Dismiss' : 'Close report'}
            </Button>
          </div>
        )}
      </div>
    </li>
  );
}

export function CommunityPictureReviewQueue(): ReactElement {
  const waiting = useQuery({
    queryKey: queryKeys.submissions.communityPictureReviews(),
    queryFn: () => api.adminListCommunityPictureReviews(),
  });
  const reports = useQuery({
    queryKey: queryKeys.submissions.communityPictureReports(),
    queryFn: () => api.adminListCommunityPictureReports(),
  });
  const count = (waiting.data?.total ?? 0) + (reports.data?.reports.length ?? 0);

  return (
    <section className="mt-10" aria-labelledby="community-picture-reviews">
      <div className="mb-4 flex items-center gap-3">
        <h2 id="community-picture-reviews" className="text-xl font-semibold">
          Shared pictures
        </h2>
        <NotificationBadge count={count} aria-label="Shared pictures awaiting review" />
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        Pictures people shared in apps that the automatic check could not clear — or every new one,
        where checking is manual — and pictures people reported.
      </p>

      <h3 className="mb-2 text-sm font-medium">
        Waiting for a decision
        {waiting.data && waiting.data.total > waiting.data.assets.length && (
          <span className="font-normal text-muted-foreground">
            {' '}
            · showing the oldest {waiting.data.assets.length} of {waiting.data.total}
          </span>
        )}
      </h3>
      {waiting.isLoading && <p className="text-muted-foreground">Loading…</p>}
      {waiting.error && (
        <p className="text-destructive">Failed to load: {errorMessage(waiting.error)}</p>
      )}
      {waiting.data && waiting.data.assets.length === 0 && (
        <Card className="p-6 text-center">
          <p className="text-muted-foreground">No picture is waiting.</p>
        </Card>
      )}
      {waiting.data && waiting.data.assets.length > 0 && (
        <Card className="overflow-hidden">
          <ul className="divide-y divide-border">
            {waiting.data.assets.map((picture) => (
              <WaitingItem key={picture.assetId} picture={picture} />
            ))}
          </ul>
        </Card>
      )}

      <h3 className="mb-2 mt-6 text-sm font-medium">Reported</h3>
      {reports.isLoading && <p className="text-muted-foreground">Loading…</p>}
      {reports.error && (
        <p className="text-destructive">Failed to load: {errorMessage(reports.error)}</p>
      )}
      {reports.data && reports.data.reports.length === 0 && (
        <Card className="p-6 text-center">
          <p className="text-muted-foreground">No open report.</p>
        </Card>
      )}
      {reports.data && reports.data.reports.length > 0 && (
        <Card className="overflow-hidden">
          <ul className="divide-y divide-border">
            {reports.data.reports.map((report) => (
              <ReportItem key={report.reportId} report={report} />
            ))}
          </ul>
        </Card>
      )}
    </section>
  );
}
